import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IdentityMediaPager } from '@/components/identities/IdentityMediaPager'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { DecisionErrorKey } from '@/features/face-matching/errors'
import type { FaceMatchingIdentityDetail } from '@/services/api/face-matching'
import { FaceActionNotice } from './FaceActionNotice'
import { FaceGalleryItem } from './FaceGalleryItem'
import { facesSummary } from './faces-card'

/** Which face is busy, and with what — one action per face at a time. */
export interface FacesCardState {
  /** The face the avatar shows today. */
  mainFaceId: string | null
  /** The face whose request is in flight. */
  pendingFaceId?: string | null
  isLoading?: boolean
  /**
   * The last change's refusal, as the suffix of its i18n key: the switch went
   * back or the main photo stayed, and the card says why. Null when it went through.
   */
  errorKey?: DecisionErrorKey | null
}

/** A missing callback makes that part read-only (a viewer, PRD-0032 SC-036). */
export interface FacesCardActions {
  /** Without it the switch only shows whether the face is a reference. */
  onToggleReference?: (faceId: string, reference: boolean) => void
  /** Without it no face offers the pick; the badge still names the main photo. */
  onMakeMainPhoto?: (faceId: string) => void
  onShowInGraph: (faceId: string) => void
}

export interface FacesCardProps {
  detail: FaceMatchingIdentityDetail | undefined
  state: FacesCardState
  actions: FacesCardActions
}

const BASE = 'identity.faces'
const FACES_PAGE_SIZE = 20

/**
 * The identity's confirmed faces with their reference switch and main photo pick.
 * Pending suggestions live with the star and candidate table in Possible matches.
 * A change the server refused is said above the faces; a viewer, who is offered
 * neither change, is told why there instead.
 *
 * Pure — every request lives in `useIdentityFaces`, so the card can be told a
 * story with mock data.
 */
export function FacesCard({ detail, state, actions }: FacesCardProps) {
  const { t } = useTranslation()
  const summary = facesSummary(detail)
  const references = detail?.references ?? []
  // Only where a face would have offered a change is there something to explain.
  const readOnly = references.length > 0 && !(actions.onToggleReference && actions.onMakeMainPhoto)
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(references.length / FACES_PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)

  useEffect(() => setPage(1), [detail?.person_id])
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const visibleReferences = references.slice(
    (currentPage - 1) * FACES_PAGE_SIZE,
    currentPage * FACES_PAGE_SIZE
  )

  return (
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex flex-wrap items-center gap-2">
          {t(`${BASE}.title`)}
          <Badge variant="muted">{t(`${BASE}.confirmedCount`, { count: summary.confirmed })}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h3 className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
            {t(`${BASE}.confirmed`)}
          </h3>
          <FaceActionNotice copy={BASE} errorKey={state.errorKey ?? null} readOnly={readOnly} />
          {state.isLoading ? (
            <p className="text-sm text-muted-foreground">{t(`${BASE}.loading`)}</p>
          ) : null}
          {!state.isLoading && references.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t(`${BASE}.noConfirmed`)}</p>
          ) : null}
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,17rem),1fr))] gap-3">
            {visibleReferences.map(reference => (
              <FaceGalleryItem
                key={reference.face_id}
                reference={reference}
                state={state}
                actions={actions}
              />
            ))}
          </ul>
          <IdentityMediaPager
            pagination={{
              label: t(`${BASE}.title`),
              page: currentPage,
              pageSize: FACES_PAGE_SIZE,
              total: references.length,
            }}
            onPageChange={setPage}
          />
        </section>
      </CardContent>
    </Card>
  )
}
